import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell } from "lucide-react";
import type { AppNotification } from "@/lib/types";
import { formatDistanceToNow } from "date-fns";
import { ar as arLocale } from "date-fns/locale";
import { useDict, useLocale } from "@/lib/i18n/context";

export function NotificationsBell() {
  const t = useDict();
  const { locale } = useLocale();
  const dateLocale = locale === "ar" ? arLocale : undefined;
  const [items, setItems] = useState<AppNotification[]>([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const res = await fetch("/api/notifications", {
          credentials: "include",
          cache: "no-store",
        });
        if (!res.ok) return;
        const data = await res.json();
        if (active) setItems(data.items ?? []);
      } catch {
        /* ignore */
      }
    }
    load();
    const id = setInterval(load, 15000);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, []);

  const unread = items.filter((n) => !n.read).length;

  async function markRead() {
    if (unread === 0) return;
    setItems((prev) => prev.map((n) => ({ ...n, read: true })));
    await fetch("/api/notifications", {
      method: "POST",
      credentials: "include",
    }).catch(() => {});
  }

  return (
    <div className="relative">
      <button
        onClick={() => {
          setOpen((o) => !o);
          if (!open) markRead();
        }}
        className="relative flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-card hover:bg-surface"
        aria-label="Notifications"
      >
        <Bell className="h-5 w-5 text-muted" />
        {unread > 0 && (
          <span className="absolute -end-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
            {unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute end-0 z-50 mt-2 w-80 overflow-hidden rounded-2xl border border-border bg-card shadow-xl">
          <div className="border-b border-border px-4 py-3 text-sm font-semibold">
            {t.notifications.title}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-muted">
                {t.notifications.empty}
              </p>
            ) : (
              items.map((n) => (
                <Link
                  key={n.id}
                  to={n.interview_id ? `/interviews/${n.interview_id}` : "#"}
                  onClick={() => setOpen(false)}
                  className="block border-b border-border px-4 py-3 hover:bg-surface"
                >
                  <p className="text-sm font-medium">{n.body}</p>
                  <p className="mt-0.5 text-xs text-muted">
                    {formatDistanceToNow(new Date(n.created_at), {
                      addSuffix: true,
                      locale: dateLocale,
                    })}
                  </p>
                </Link>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
